import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../lib/supabase';
import { AccessibleHome, getAccessibleHomes } from '../lib/homeMembership';

const ACTIVE_HOME_KEY = 'active_home_id';

interface ActiveHomeContextValue {
  homes: AccessibleHome[];
  activeHome: AccessibleHome | null;
  setActiveHomeById: (homeId: string) => void;
  refreshHomes: () => Promise<void>;
  loading: boolean;
}

const ActiveHomeContext = createContext<ActiveHomeContextValue | undefined>(undefined);

const readStoredActiveHomeId = async (): Promise<string | null> => {
  if (Platform.OS === 'web') {
    return localStorage.getItem(ACTIVE_HOME_KEY);
  }

  return AsyncStorage.getItem(ACTIVE_HOME_KEY);
};

const persistActiveHomeId = async (homeId: string | null): Promise<void> => {
  if (Platform.OS === 'web') {
    if (homeId) {
      localStorage.setItem(ACTIVE_HOME_KEY, homeId);
    } else {
      localStorage.removeItem(ACTIVE_HOME_KEY);
    }
    return;
  }

  if (homeId) {
    await AsyncStorage.setItem(ACTIVE_HOME_KEY, homeId);
  } else {
    await AsyncStorage.removeItem(ACTIVE_HOME_KEY);
  }
};

export const ActiveHomeProvider = ({ children }: { children: React.ReactNode }) => {
  const [homes, setHomes] = useState<AccessibleHome[]>([]);
  const [activeHomeId, setActiveHomeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshHomes = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) {
        setHomes([]);
        setActiveHomeId(null);
        await persistActiveHomeId(null);
        return;
      }

      const nextHomes = await getAccessibleHomes(session.user.id);
      setHomes(nextHomes);

      if (nextHomes.length === 0) {
        setActiveHomeId(null);
        await persistActiveHomeId(null);
        return;
      }

      const storedHomeId = await readStoredActiveHomeId();
      const preferredHomeId = activeHomeId || storedHomeId;
      // If there's a stored/in-memory preference, use it. Otherwise prefer the home
      // the user owns (owner_id matches) before falling back to the first home in the list.
      const resolvedHome =
        (preferredHomeId ? nextHomes.find((home) => home.id === preferredHomeId) : null) ||
        nextHomes.find((home) => home.owner_id === session.user.id) ||
        nextHomes[0];

      setActiveHomeId(resolvedHome.id);
      await persistActiveHomeId(resolvedHome.id);
    } catch (error) {
      console.error('Error refreshing active homes:', error);
      setHomes([]);
      setActiveHomeId(null);
    } finally {
      setLoading(false);
    }
  }, [activeHomeId]);

  useEffect(() => {
    refreshHomes().catch((error) => {
      console.error('Error refreshing active homes on mount:', error);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      refreshHomes().catch((error) => {
        console.error('Error refreshing active homes after auth change:', error);
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [refreshHomes]);

  const setActiveHomeById = useCallback((homeId: string) => {
    setActiveHomeId(homeId);
    persistActiveHomeId(homeId).catch((error) => {
      console.error('Error persisting active home id:', error);
    });
  }, []);

  const value = useMemo<ActiveHomeContextValue>(() => ({
    homes,
    activeHome: homes.find((home) => home.id === activeHomeId) || null,
    setActiveHomeById,
    refreshHomes,
    loading,
  }), [homes, activeHomeId, setActiveHomeById, refreshHomes, loading]);

  return (
    <ActiveHomeContext.Provider value={value}>
      {children}
    </ActiveHomeContext.Provider>
  );
};

export const useActiveHome = () => {
  const context = useContext(ActiveHomeContext);
  if (!context) {
    throw new Error('useActiveHome must be used within ActiveHomeProvider');
  }
  return context;
};
